// ProfileInfoBadges.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { withTranslation } from 'react-i18next';

import Badge from '../component/Badge.jsx';

import getLeaderboardBadgeData from '../resource/getLeaderboardBadgeData.js';
import leaderboardIcons from '../resource/getLeaderboardIcons.js';
import {
  RANGE_24H,
  RANGE_7D,
  RANGE_30D,
} from '../resource/leaderboardConstants.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { PUBLIC } from '../resource/resourceUrlTypeConstants.js';
import getPublicSrcSet from '../resource/getPublicSrcSet.js';
import { MediaAssetFormat } from '../resource/getMediaAsset.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import { color } from '../style/variables.js';

export const ProfileInfoBadges = ({
  badges,
  t,
  publicBadgeUrlPath,
  publicLeaderboardUrlPath,
}) => {
  return (
    <StyledProfileInfoBadge>
      {badges.map(badge => {
        const { isLeaderboardBadge, region, category, range, place } =
          getLeaderboardBadgeData({ badgeName: badge });
        if (isLeaderboardBadge) {
          const iconSource = getResourceUrl({
            path: publicLeaderboardUrlPath,
            endpoint: `/${category}.png`,
            resourceType: PUBLIC,
          });
          const [avifSrcSet, webpSrcSet, jpgSrcSet] = [
            MediaAssetFormat.AVIF,
            MediaAssetFormat.WEBP,
            MediaAssetFormat.JPG,
          ].map(format =>
            getPublicSrcSet({ href: iconSource, size: 64, format })
          );
          const placeIcon = leaderboardIcons.place[place];
          return (
            <BadgeLink
              key={badge}
              to={`/leaderboard/${region}/${category}/${range}`}
            >
              <Badge
                icon={
                  <BadgeIcon range={range} alt={`${category} icon`} aria-hidden>
                    <source {...avifSrcSet} type="image/avif" />
                    <source {...webpSrcSet} type="image/webp" />
                    <img {...jpgSrcSet} type="image/jpg" />
                  </BadgeIcon>
                }
                title={
                  <LeaderboardPlaceWrapper>
                    {t(`leaderboard_${region}`, {
                      ns: TranslationNamespace.PROFILE,
                    })}
                    <PlaceNoText>No.</PlaceNoText>
                    {place}
                    {placeIcon && <PlaceIcon src={placeIcon} />}
                  </LeaderboardPlaceWrapper>
                }
              />
            </BadgeLink>
          );
        }
        return (
          <BadgeLink key={badge} to={`/story-categories/discover_${badge}`}>
            <Badge
              icon={
                getResourceUrl({
                  path: publicBadgeUrlPath,
                  endpoint: `/${badge}.png`,
                  resourceType: PUBLIC,
                }).href
              }
              title={
                <BadgeTitleWrapper>
                  {t(badge, {
                    ns: TranslationNamespace.PROFILE,
                  })}
                </BadgeTitleWrapper>
              }
            />
          </BadgeLink>
        );
      })}
    </StyledProfileInfoBadge>
  );
};

ProfileInfoBadges.propTypes = {
  badges: PropTypes.array,
  publicBadgeUrlPath: PropTypes.string,
  publicLeaderboardUrlPath: PropTypes.string,
  t: PropTypes.func.isRequired,
};

ProfileInfoBadges.defaultProps = {
  badges: [],
  publicBadgeUrlPath: '',
  publicLeaderboardUrlPath: '',
};

const StyledProfileInfoBadge = styled.div``;

const BadgeLink = styled(Link)`
  margin-right: 4px;
  margin-bottom: 6px;
  display: inline-block;
`;

const BadgeTitleWrapper = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
`;

const BadgeIcon = styled.picture`
  display: inline-block;
  border-radius: 50%;
  padding: 2px;
  width: 18px;
  height: 18px;
  ${({ range }) => {
    if (range === RANGE_24H) {
      return `background-color: ${color.peach};`;
    } else if (range === RANGE_7D) {
      return `background-color: ${color.green};`;
    } else if (range === RANGE_30D) {
      return `background-color: ${color.sky};`;
    }
  }}
  overflow: hidden;

  > img {
    width: 100%;
    height: 100%;
    vertical-align: super;
  }
`;

const LeaderboardPlaceWrapper = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
`;

const PlaceNoText = styled.span`
  margin-left: 2px;
  margin-right: 2px;
  margin-bottom: -1px;
  font-size: 10px;
`;

const PlaceIcon = styled.img`
  margin-bottom: -1px;
  width: 14px;
  height: 14px;
`;

export default withTranslation()(ProfileInfoBadges);
